/**
 * cuentas-pagar.schemas.ts
 * Local Zod response schemas for the Cuentas a Pagar (AP) responses that
 * have NO shared contract to import (design.md D2, APU-6.2/APU-3.3).
 *
 * WHY this file exists: the backend maps rows to responses by hand
 * (`toComprobanteResponse`, `packages/modules/sumi/src/http/cuentas-pagar/
 * comprobante.routes.ts:61-88`; `toConfigResponse`/`toTipoRetencionResponse`,
 * `ap-admin.routes.ts:41-55`; `GetAgingUseCase.ts:21-28` for `GET /aging`;
 * `ListMovimientosProveedorUseCase.ts`/`ListComprobantesPendientesProveedorUseCase.ts`
 * for the two T2/T3 proveedor-detail reads, odd/tasks/cuentas-pagar-detalle-imputacion-reporte.md)
 * and there is today NO Zod contract in `@suite/contracts-sumi` for
 * any of these READ shapes. Without a runtime check, a field the backend
 * stops sending would compile fine and only break (or lie) on screen.
 * `parsed()` in `cuentas-pagar.ts` applies these schemas to every response
 * BEFORE it reaches a composable/view.
 *
 * Types are NEVER declared by hand here — every exported type is `z.infer`
 * of its schema, so validation and type can never drift apart (D2).
 *
 * The lote response (`POST /comprobantes/lote`) is the ONE AP response with a
 * SHARED contract (`ComprobanteLoteResponseSchema`, `@suite/contracts-sumi`)
 * — it does NOT get a local twin here; that would be the second implementation
 * of the same concept (root CLAUDE.md).
 *
 * Follow-up (R3, design.md): these 4 schemas are the literal prototype for a
 * future backend change that moves them into `contracts-cuentas-pagar` as the
 * route files' real output contract — when that happens, this file is deleted,
 * not kept alongside the shared version.
 *
 * Spec: openspec/changes/add-cuentas-pagar-ui/specs/cuentas-pagar-ui/spec.md APU-6.2, APU-3.3
 * Design: openspec/changes/add-cuentas-pagar-ui/design.md D2
 */
import { z } from 'zod';
import { OcMatchAdvertenciaSchema, ModoCalculoSchema, GananciasConceptoSchema } from '@suite/contracts-sumi';

// ---------------------------------------------------------------------------
// cxp-distribucion-cc-por-linea-oc — shared shape for the persisted
// distribution (create response) and the live preview (computed client-side
// with the SAME `agruparDistribucionPorCentroCosto`, `@suite/contracts-sumi`).
// ---------------------------------------------------------------------------

export const ApDistribucionPorCentroCostoSchema = z.object({
  costCenterId: z.string().nullable(),
  monto: z.number(),
});

export type ApDistribucionPorCentroCosto = z.infer<typeof ApDistribucionPorCentroCostoSchema>;

/** Same split grouped by the SA's Área / Yacimiento (`agruparDistribucionPorArea`). */
export const ApDistribucionPorAreaSchema = z.object({
  areaId: z.string().nullable(),
  monto: z.number(),
});

// ---------------------------------------------------------------------------
// GET /sumi/cuentas-pagar/aging — GetAgingUseCase.ts:19-28.
// `pendiente` is a NUMBER (`Number(balance.pendiente)` in the use case), NOT
// the string round-trip that comprobante amounts use.
// ---------------------------------------------------------------------------

export const ApAgingBucketSchema = z.enum([
  'vigente',
  'vencido_1_30',
  'vencido_31_60',
  'vencido_61_90',
  'vencido_90_mas',
]);

export const ApAgingRowSchema = z.object({
  comprobanteId: z.string(),
  supplierId: z.string(),
  pendiente: z.number(),
  fechaVencimiento: z.string().nullable(),
  diasVencido: z.number().nullable(),
  bucket: ApAgingBucketSchema,
});

export const ApAgingResponseSchema = z.object({
  items: z.array(ApAgingRowSchema),
});

export type ApAgingBucket = z.infer<typeof ApAgingBucketSchema>;
export type ApAgingRow = z.infer<typeof ApAgingRowSchema>;
export type ApAgingResponse = z.infer<typeof ApAgingResponseSchema>;

// ---------------------------------------------------------------------------
// POST /sumi/cuentas-pagar/comprobantes (201) — toComprobanteResponse,
// comprobante.routes.ts:61-88. The body is the comprobante PLANO
// (routes.ts:105), NOT `{ comprobante }`.
// OJO: `montoTotal` and `tipoCambio` are STRINGS (numeric(18,2) round-trips
// as text); `letra`/`puntoVenta`/`numero`/`cuitProveedor`/`tipoCambio`/
// `fechaVencimiento`/`respaldoTipo`/`respaldoId`/`destinoCostoTipo`/
// `destinoCostoId` are all nullable — mirrors `ComprobanteResponseRow`
// 1:1 (comprobante.routes.ts:34-59).
// ---------------------------------------------------------------------------

export const ApComprobanteResponseSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  payerLegalEntityId: z.string(),
  supplierId: z.string(),
  tipo: z.string(),
  letra: z.string().nullable(),
  puntoVenta: z.string().nullable(),
  numero: z.string().nullable(),
  cuitProveedor: z.string().nullable(),
  llaveExterna: z.string(),
  llaveFuente: z.string(),
  origen: z.string(),
  moneda: z.string(),
  tipoCambio: z.string().nullable(),
  montoTotal: z.string(),
  /** AP-1.1b — null on pre-change rows; `iva` is the derived total − neto (null while the neto is). */
  montoNeto: z.string().nullable(),
  iva: z.string().nullable(),
  /** Migration 0041 — perceptions (signed like the total) and "Detalle de pago"; defaults cover an older backend. */
  percepciones: z.array(z.object({ tipo: z.string(), monto: z.string() })).default([]),
  detallePago: z.string().nullable().default(null),
  /** Migration 0044 — Ganancias concept and the bienes de cambio part of a 'mixto' neto; defaults cover an older backend. */
  gananciasConcepto: GananciasConceptoSchema.nullable().default(null),
  netoBienes: z.string().nullable().default(null),
  fechaEmision: z.string(),
  fechaVencimiento: z.string().nullable(),
  vencimientoFuente: z.string(),
  respaldoTipo: z.string().nullable(),
  respaldoId: z.string().nullable(),
  /** Migration 0042 — the attached file (the certification); default covers an older backend. */
  adjuntoFileId: z.string().nullable().default(null),
  destinoCostoTipo: z.string().nullable(),
  destinoCostoId: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string(),
  /** APR-1.3, D7 — always present on the real response; `.default([])` covers an older client/response shape. */
  advertencias: z.array(OcMatchAdvertenciaSchema).default([]),
  /** cxp-distribucion-cc-por-linea-oc — always present; `.default([])` covers an older client/response shape. */
  distribucion: z.array(ApDistribucionPorCentroCostoSchema).default([]),
  distribucionPorArea: z.array(ApDistribucionPorAreaSchema).default([]),
});

export type ApComprobanteResponse = z.infer<typeof ApComprobanteResponseSchema>;

/** GET /sumi/cuentas-pagar/comprobantes/:id — pre-fills the edit form (ap-anular-editar-factura). */
export const ApComprobanteDetalleSchema = ApComprobanteResponseSchema.omit({ advertencias: true, distribucion: true, distribucionPorArea: true }).extend({
  /** OC lines the comprobante was distributed over — the checklist comes back as it was. */
  lineasOc: z.array(z.string()),
  modificable: z.boolean(),
});

export type ApComprobanteDetalle = z.infer<typeof ApComprobanteDetalleSchema>;

// ---------------------------------------------------------------------------
// GET/PUT /sumi/cuentas-pagar/config — toConfigResponse, ap-admin.routes.ts:41-46.
// ---------------------------------------------------------------------------

export const ApConfigResponseSchema = z.object({
  fechaCorteSaldoInicial: z.string().nullable(),
  plazoPagoPorDefectoDias: z.number().nullable(),
  /** AP-9.1/AP-9.2, D8 — numeric(5,2), round-trips as text (e.g. "5.00"). `.default(null)` for an older response shape. */
  toleranciaMontoOcPct: z.string().nullable().default(null),
});

export type ApConfigResponse = z.infer<typeof ApConfigResponseSchema>;

// ---------------------------------------------------------------------------
// GET /sumi/cuentas-pagar/ordenes-compra — integracion-compras-cuentas-pagar (D11/D12).
// ---------------------------------------------------------------------------

export const ApOrdenCompraOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  status: z.string(),
  statusLabel: z.string(),
  /** Net of VAT. */
  total: z.string(),
  /** VAT at the OC lines' frozen rates (21 % and 10,5 % summed). */
  ivaTotal: z.string(),
  currency: z.string(),
  emittedAt: z.string(),
});

export type ApOrdenCompraOption = z.infer<typeof ApOrdenCompraOptionSchema>;

export const ApOrdenCompraSearchResponseSchema = z.object({
  items: z.array(ApOrdenCompraOptionSchema),
  truncado: z.boolean(),
});

export type ApOrdenCompraSearchResponse = z.infer<typeof ApOrdenCompraSearchResponseSchema>;

// ---------------------------------------------------------------------------
// GET /sumi/cuentas-pagar/ordenes-compra/:id/lineas — cxp-distribucion-cc-por-linea-oc.
// `qty`/`unitPrice`/`lineTotal` are STRINGS, same numeric round-trip
// convention as every other AP amount. `costCenterName` is `null` when
// `costCenterId` is null OR failed to resolve — never a raw uuid shown.
// ---------------------------------------------------------------------------

export const ApPayableOrderLineOptionSchema = z.object({
  poLineId: z.string(),
  description: z.string(),
  qty: z.string(),
  unitPrice: z.string(),
  lineTotal: z.string(),
  costCenterId: z.string().nullable(),
  costCenterName: z.string().nullable(),
  areaId: z.string().nullable().default(null),
  areaName: z.string().nullable().default(null),
});

export type ApPayableOrderLineOption = z.infer<typeof ApPayableOrderLineOptionSchema>;

export const ApPayableOrderLinesResponseSchema = z.object({
  items: z.array(ApPayableOrderLineOptionSchema),
});

export type ApPayableOrderLinesResponse = z.infer<typeof ApPayableOrderLinesResponseSchema>;

// ---------------------------------------------------------------------------
// GET /sumi/cuentas-pagar/retenciones/tipos → { items } — toTipoRetencionResponse,
// ap-admin.routes.ts:48-55. Also reused for the 201 of POST (single row).
// ---------------------------------------------------------------------------

export const ApTipoRetencionResponseSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  activo: z.boolean(),
  /** AP-8.2 — closed calculation mode + only its parameters (shared discriminated union). */
  calculo: ModoCalculoSchema,
  createdAt: z.string(),
});

export const ApTipoRetencionListSchema = z.object({
  items: z.array(ApTipoRetencionResponseSchema),
});

export type ApTipoRetencionResponse = z.infer<typeof ApTipoRetencionResponseSchema>;
export type ApTipoRetencionList = z.infer<typeof ApTipoRetencionListSchema>;

// ---------------------------------------------------------------------------
// GET /sumi/cuentas-pagar/proveedores/:id/movimientos (T2) —
// ListMovimientosProveedorUseCase.ts. `monto`/`balanceAfter` are STRINGS,
// same numeric(18,2) round-trip convention as `ApComprobanteResponseSchema`.
// ---------------------------------------------------------------------------

export const ApMovimientoComprobanteInfoSchema = z.object({
  tipo: z.string(),
  letra: z.string().nullable(),
  puntoVenta: z.string().nullable(),
  numero: z.string().nullable(),
});

export const ApMovimientoProveedorRowSchema = z.object({
  id: z.string(),
  comprobanteId: z.string().nullable(),
  tipo: z.string(),
  monto: z.string(),
  balanceAfter: z.string(),
  motivo: z.string().nullable(),
  createdAt: z.string(),
  comprobante: ApMovimientoComprobanteInfoSchema.nullable(),
  /** retenciones-en-el-pago — order number of a `pago` / `retencion` movement; name of the type of a `retencion`. */
  ordenPagoNumero: z.number().int().nullable(),
  tipoRetencionNombre: z.string().nullable(),
});

export const ApMovimientosProveedorResponseSchema = z.object({
  items: z.array(ApMovimientoProveedorRowSchema),
});

export type ApMovimientoProveedorRow = z.infer<typeof ApMovimientoProveedorRowSchema>;
export type ApMovimientosProveedorResponse = z.infer<typeof ApMovimientosProveedorResponseSchema>;

// ---------------------------------------------------------------------------
// GET /sumi/cuentas-pagar/proveedores/:id/comprobantes-pendientes (T3) —
// ListComprobantesPendientesProveedorUseCase.ts. `montoTotal`/`pendiente` are
// STRINGS, same numeric(18,2) round-trip convention as `ApComprobanteResponseSchema`.
// ---------------------------------------------------------------------------

export const ApComprobantePendienteRowSchema = z.object({
  id: z.string(),
  tipo: z.string(),
  letra: z.string().nullable(),
  puntoVenta: z.string().nullable(),
  numero: z.string().nullable(),
  fechaVencimiento: z.string().nullable(),
  montoTotal: z.string(),
  pendiente: z.string(),
});

export const ApComprobantesPendientesResponseSchema = z.object({
  items: z.array(ApComprobantePendienteRowSchema),
});

export type ApComprobantePendienteRow = z.infer<typeof ApComprobantePendienteRowSchema>;
export type ApComprobantesPendientesResponse = z.infer<typeof ApComprobantesPendientesResponseSchema>;

// ---------------------------------------------------------------------------
// GET /sumi/cuentas-pagar/proveedores/:id/saldo (T4, saldo.routes.ts) —
// GetSaldoProveedorUseCase.ts. `saldo` is a plain running-balance NUMBER
// (`Number(row.saldo)` in the use case) — unlike the comprobante amount
// fields above, this one is NOT a numeric-string round-trip. Can be either
// sign (a supplier current account may legitimately sit on either side, no
// non-negative constraint). Was declared `Promise<unknown>` with no schema
// until T4 (SupplierDetailView) became its first real consumer.
// ---------------------------------------------------------------------------

export const ApSaldoProveedorResponseSchema = z.object({
  proveedorId: z.string(),
  saldo: z.number(),
});

export type ApSaldoProveedorResponse = z.infer<typeof ApSaldoProveedorResponseSchema>;

// GET /sumi/cuentas-pagar/pagos/:id — the response schema (`OrdenPagoDetalleSchema`) now lives in
// `@suite/contracts-sumi` (retenciones-en-el-pago APR-7): no local copy here.
